import {
    AGREEMENTS_ERROR,
    AGREEMENTS_FETCH_START,
    AGREEMENTS_FETCH_SUCCESS,
    AgreementsAction
} from 'actions/agreements/AgreementsActionsTypes';
import {
    convertAgreements,
    convertToAgreementSignature,
    convertToAgreementSignatureDto
} from 'actions/agreements/AgreementsDataConverter';
import { AgreementDto, AgreementSignatureDto } from 'actions/agreements/AgreementsDtos';
import {
    markOnlineBookingAgreementSignaturePersisted,
    updateOnlineBookingAgreementSignature
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { ApiClient, ApiPublicClient, createTokenConfig } from 'api/ApiClient';
import { serializeToFormData } from 'api/serializer';
import { AxiosError, AxiosResponse } from 'axios';
import moment from 'moment-timezone';
import { getOnlineBookingDepositRequired } from 'reducers/onlineBooking/OnlineBookingSelectors';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { RootAction } from 'types/Actions';
import { Agreement, AgreementSignature } from 'types/Agreement';

const agreementsUrl = 'agreements';
const signaturesUrl = 'signatures';
const findOrCreateSignatureUrl = 'signature/find_or_create';

const retryDelays = [500, 1500];

const errorHandler = (error: unknown): AgreementsAction => {
    console.error(error);

    return {
        type: AGREEMENTS_ERROR
    };
};

export const fetchAgreementsThunk = (
    customerId: number
): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        dispatch(fetchStart());

        const marketplaceId = getState().marketplace.data.id;

        try {
            const response: AxiosResponse<Array<AgreementDto>> = await ApiPublicClient.get(
                agreementsUrl,
                {
                    ...createTokenConfig(),
                    params: {
                        customer_id: customerId,
                        marketplace_id: marketplaceId
                    }
                }
            );

            dispatch(fetchSuccess(convertAgreements(response.data)));
        } catch (error) {
            dispatch(errorHandler(error));
        }
    };
};

const fetchStart = (): AgreementsAction => {
    return {
        type: AGREEMENTS_FETCH_START
    };
};

const fetchSuccess = (agreements: Array<Agreement>): AgreementsAction => {
    return {
        type: AGREEMENTS_FETCH_SUCCESS,
        payload: {
            agreements
        }
    };
};

const delay = (milliseconds: number): Promise<void> =>
    new Promise(resolve => setTimeout(resolve, milliseconds));

const withRetries = async <T>(
    task: () => Promise<T>,
    shouldRetry: (error: unknown) => boolean = () => true
): Promise<T> => {
    for (let attempt = 0; ; attempt++) {
        try {
            return await task();
        } catch (error) {
            if (attempt >= retryDelays.length || !shouldRetry(error)) {
                throw error;
            }

            await delay(retryDelays[attempt]);
        }
    }
};

const hasNoResponse = (error: unknown): boolean => !(error as AxiosError).response;

const findOrCreateSignature = async (
    customerId: number,
    agreementId: number,
    marketplaceId: number
): Promise<AgreementSignature> => {
    const response: AxiosResponse<AgreementSignatureDto> = await ApiClient.put(
        findOrCreateSignatureUrl,
        {
            customer_id: customerId,
            agreement_id: agreementId,
            marketplace_id: marketplaceId
        },
        createTokenConfig()
    );

    return convertToAgreementSignature(response.data);
};

const signSignature = (signature: AgreementSignature, customerSignature: File) =>
    ApiClient.patch(
        `${signaturesUrl}/${signature.id}`,
        serializeToFormData({
            signature: convertToAgreementSignatureDto({
                ...signature,
                sentAt: signature.sentAt || moment(),
                signedAt: moment(),
                customerSignature
            })
        }),
        createTokenConfig()
    );

/**
 * Every signing PATCH makes the backend email the signed PDF, and the request can fail after the
 * signature was saved. So later attempts reuse the same record, and the PATCH is only repeated
 * when the server never answered.
 */
export const persistAgreementSignaturesThunk = (): ThunkAction<
    Promise<Array<number>>,
    RootState,
    null,
    RootAction
> => {
    return async (dispatch, getState) => {
        const state = getState();

        const marketplaceId = state.marketplace.data.id;
        const customerId = Number(state.onlineBooking.customer.id);
        const pendingSignatures = state.onlineBooking.agreementSignatures.filter(
            signature => !signature.persisted && !signature.saveFailed
        );

        if (!customerId || pendingSignatures.length === 0) {
            return [];
        }

        const results = await Promise.all(
            pendingSignatures.map(async ({ agreementId, customerSignature, signature: known }) => {
                let signature = known;

                if (!signature) {
                    try {
                        signature = await withRetries(() =>
                            findOrCreateSignature(customerId, agreementId, marketplaceId)
                        );
                    } catch (error) {
                        console.error(error);

                        return agreementId;
                    }

                    dispatch(updateOnlineBookingAgreementSignature(agreementId, { signature }));
                }

                const signatureToSign = signature;

                try {
                    await withRetries(
                        () => signSignature(signatureToSign, customerSignature),
                        hasNoResponse
                    );
                } catch (error) {
                    console.error(error);

                    if (!hasNoResponse(error)) {
                        dispatch(
                            updateOnlineBookingAgreementSignature(agreementId, {
                                saveFailed: true
                            })
                        );
                    }

                    return agreementId;
                }

                dispatch(markOnlineBookingAgreementSignaturePersisted(agreementId));

                return null;
            })
        );

        return results.filter((agreementId): agreementId is number => agreementId !== null);
    };
};

/**
 * Signing is what emails the agreement, so a booking with a deposit waits for the Success step,
 * which is only reached once the payment went through.
 */
export const persistAgreementSignaturesUnlessDepositRequiredThunk = (): ThunkAction<
    Promise<Array<number>>,
    RootState,
    null,
    RootAction
> => {
    return async (dispatch, getState) => {
        if (getOnlineBookingDepositRequired(getState())) {
            return [];
        }

        return dispatch(persistAgreementSignaturesThunk());
    };
};
